import { createContext, type PropsWithChildren, useCallback, useContext, useState } from 'react'
import { toast } from 'sonner'

import { usePatchQuotingProspectsProspectIdQuotesQuoteIdSettings as useUpdateQuoteSettings } from 'utils/web_api'

type QuoteSettingsContextType = {
  includeEmployeeCostImpact: boolean
  setincludeEmployeeCostImpact: (value: boolean) => void
  hasEmployeeCostImpactSettingAccess: boolean
  isUpdating: boolean
}

const QuoteSettingsContext = createContext<QuoteSettingsContextType>({
  includeEmployeeCostImpact: true,
  setincludeEmployeeCostImpact: () => {},
  hasEmployeeCostImpactSettingAccess: false,
  isUpdating: false,
})

export function useQuoteSettings () {
  return useContext(QuoteSettingsContext)
}

type QuoteSettingsProviderProps = PropsWithChildren<{
  hasEmployeeCostImpactSettingAccess: boolean
  prospectId: string
  quoteId: string
  defaultincludeEmployeeCostImpact: boolean
}>

export function QuoteSettingsProvider ({
  hasEmployeeCostImpactSettingAccess,
  defaultincludeEmployeeCostImpact,
  prospectId,
  quoteId,
  children
}: QuoteSettingsProviderProps) {
  const [includeEmployeeCostImpact, setincludeEmployeeCostImpact] = useState(defaultincludeEmployeeCostImpact)

  const { mutate: updateSettings, isPending: isUpdating } = useUpdateQuoteSettings({
    mutation: {
      onError: (_error, variables) => {
        // Revert on failure
        setincludeEmployeeCostImpact(!variables.data.employee_cost_impact_enabled)
        toast.error('Failed to update quote settings')
      },
    },
  })

  const setincludeEmployeeCostImpactWithApi = useCallback((value: boolean) => {
    setincludeEmployeeCostImpact(value)
    updateSettings({
      prospectId,
      quoteId,
      data: { employee_cost_impact_enabled: value },
    })
  }, [prospectId, quoteId, updateSettings])

  return (
    <QuoteSettingsContext.Provider
      value={{
        includeEmployeeCostImpact,
        setincludeEmployeeCostImpact: setincludeEmployeeCostImpactWithApi,
        hasEmployeeCostImpactSettingAccess,
        isUpdating,
      }}
    >
      {children}
    </QuoteSettingsContext.Provider>
  )
}
